import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AGENT_CONNECTORS, downloadConfig, copyToClipboard, type AgentConfig } from "../lib/connector-config";

export function AiConnector() {
  const [selected, setSelected] = useState<AgentConfig | null>(null);

  return (
    <section id="connect" className="relative px-5 md:px-10 py-24 md:py-32 max-w-7xl mx-auto">
      <div className="max-w-3xl mb-12">
        <div className="eyebrow mb-4"><span className="dot" />ai connectors</div>
        <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95]">
          one click. <span className="font-serif italic text-aurora">any agent.</span>
        </h2>
        <p className="mt-6 text-bone-dim text-base max-w-2xl">
          Connect GAIA to your favorite AI agent in seconds. Choose your agent below and we'll
          generate the config for you — just like DogeShit Wallet does for EIP-7702 wallets.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {AGENT_CONNECTORS.map((agent) => (
          <button
            key={agent.id}
            onClick={() => setSelected(agent)}
            className="ink-card group text-left p-5 cursor-pointer hover:border-teal-300/30 transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`size-12 rounded-2xl bg-gradient-to-br ${agent.accent} grid place-items-center font-mono text-lg text-ink-1000 shadow-[0_8px_24px_-8px_rgba(64,176,194,0.5)] font-bold`}>
                {agent.icon}
              </div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="opacity-0 group-hover:opacity-100 transition-opacity text-teal-300">
                <path d="M6 3 L11 8 L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="font-display text-xl tracking-tight">{agent.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim mt-1">{agent.description}</div>
          </button>
        ))}
      </div>

      <div className="mt-8 p-6 rounded-2xl border border-white/10 bg-ink-900/40">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-block w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim">how it works</span>
        </div>
        <ol className="space-y-2 text-sm text-bone-dim">
          <li className="flex gap-3">
            <span className="font-mono text-teal-300 font-bold">1</span>
            <span>Click your AI agent above — we generate the exact config file it needs.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-teal-300 font-bold">2</span>
            <span>Download the config and paste it into your agent's settings (or run the setup script).</span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-teal-300 font-bold">3</span>
            <span>Say "mint me some GAIA" — your agent calls the MCP and returns ready-to-sign tx params.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-teal-300 font-bold">4</span>
            <span>Sign with your wallet (or use your delegated EOA for gasless AI minting).</span>
          </li>
        </ol>
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              className="fixed inset-0 bg-ink-1000/80 backdrop-blur-sm"
              style={{ zIndex: 9998 }}
              onClick={() => setSelected(null)}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              style={{ zIndex: 9999 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(600px,94vw)] max-h-[88vh] overflow-y-auto card p-0"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-4">
                  <div className={`size-12 rounded-2xl bg-gradient-to-br ${selected.accent} grid place-items-center font-mono text-lg text-ink-1000 font-bold`}>
                    {selected.icon}
                  </div>
                  <div>
                    <div className="font-display text-2xl">{selected.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim">{selected.description}</div>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="size-10 rounded-xl hover:bg-white/5 transition-colors flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Quick Actions */}
              <div className="px-6 py-4 border-b border-white/10 flex gap-3">
                <button
                  onClick={() => downloadConfig(selected)}
                  className="btn btn-primary"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mr-2">
                    <path d="M8 2 V10 M4 7 L8 11 L12 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 13 H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                  Download Config
                </button>
                {selected.deepLink && (
                  <a
                    href={selected.deepLink}
                    className="btn btn-ghost"
                    onClick={() => {
                      // Try to open the app, if it fails show instructions
                      setTimeout(() => {
                        alert("If Claude Desktop didn't open automatically, follow the manual steps below.");
                      }, 500);
                    }}
                  >
                    Open App ↗
                  </a>
                )}
              </div>

              {/* Steps */}
              <div className="p-6 space-y-4">
                {selected.steps.map((step, i) => (
                  <div key={i} className="rounded-xl bg-ink-900/50 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="size-7 rounded-full bg-gradient-to-br from-teal-300 to-leaf grid place-items-center font-mono text-xs text-ink-1000 font-bold">
                        {i + 1}
                      </span>
                      <span className="font-mono text-sm font-semibold">{step.title}</span>
                    </div>
                    <p className="text-sm text-bone-dim mb-2">{step.body}</p>
                    {step.code && (
                      <div className="relative">
                        <pre className="font-mono text-[11px] text-bone bg-ink-1000/60 rounded-lg px-3 py-2.5 overflow-x-auto">{step.code}</pre>
                        <button
                          onClick={() => copyToClipboard(step.code!)}
                          className="absolute top-2 right-2 font-mono text-[9px] uppercase tracking-wider text-teal-300 hover:text-teal-200 bg-ink-1000/80 px-2 py-1 rounded"
                        >
                          copy
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim">
                  config: {selected.configFile.filename}
                </div>
                <button
                  onClick={() => copyToClipboard(selected.configFile.content)}
                  className="font-mono text-[10px] uppercase tracking-wider text-teal-300 hover:text-teal-200"
                >
                  copy raw config ↗
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
